import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, Eye, EyeOff, Shield, Database, Users } from 'lucide-react';
import AdminGlobalService, { ImpactoEliminacion, ResultadoEliminacion } from '../../services/adminGlobalService';
import { DialogShell } from '../ui/DialogShell';
import { reportarErrorInesperado } from '../../utils/reportarErrorInesperado';
import { FormErrorAlert } from '../ui/FormErrorAlert';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  tipoEntidad: 'provincia' | 'diocesis' | 'lumisial';
  entidadId: string;
  entidadNombre: string;
  onSuccess?: () => void;
}

const EliminacionDefinitivaModal: React.FC<Props> = ({
  isOpen,
  onClose,
  tipoEntidad,
  entidadId,
  entidadNombre,
  onSuccess
}) => {
  const [paso, setPaso] = useState<1 | 2 | 3 | 4>(1);
  const [loading, setLoading] = useState(false);
  const [impacto, setImpacto] = useState<ImpactoEliminacion | null>(null);
  const [confirmacionTexto, setConfirmacionTexto] = useState('');
  const [mostrarDetalles, setMostrarDetalles] = useState(false);
  const [resultado, setResultado] = useState<ResultadoEliminacion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [esAdminGlobal, setEsAdminGlobal] = useState<boolean | null>(null);
  const [verificandoPermisos, setVerificandoPermisos] = useState(false);

  useEffect(() => {
    if (isOpen) {
      resetearEstado();
      setVerificandoPermisos(true);
      verificarAdminGlobal();
    }
  }, [isOpen]);

  const resetearEstado = () => {
    setPaso(1);
    setLoading(false);
    setImpacto(null);
    setConfirmacionTexto('');
    setMostrarDetalles(false);
    setResultado(null);
    setError(null);
    setEsAdminGlobal(null);
    setVerificandoPermisos(false);
  };

  const verificarAdminGlobal = async () => {
    try {
      const admin = await AdminGlobalService.verificarAdminGlobal();
      setEsAdminGlobal(admin);
      if (!admin) {
        setError('No tienes permisos de administrador global para realizar esta acción.');
      }
    } catch (e: unknown) {
      setError('No pudimos verificar tus permisos de administrador.');
      setEsAdminGlobal(false);
      reportarErrorInesperado(e, 'No pudimos verificar tus permisos de administrador.');
    } finally {
      setVerificandoPermisos(false);
    }
  };

  const obtenerImpacto = async () => {
    setLoading(true);
    setError(null);
    try {
      const impactoData = await AdminGlobalService.obtenerImpactoEliminacion(tipoEntidad, entidadId);
      if (!impactoData) {
        setError('No se pudo obtener información de la entidad seleccionada.');
        setLoading(false);
        return;
      }
      setImpacto(impactoData);
      setPaso(2);
    } catch (e: unknown) {
      setError('No pudimos calcular el impacto de la eliminación.');
      reportarErrorInesperado(e, 'No pudimos calcular el impacto de la eliminación.');
    } finally {
      setLoading(false);
    }
  };

  const confirmarEliminacion = async () => {
    if (confirmacionTexto.trim() !== entidadNombre.trim()) {
      setError(`El texto de confirmación no es correcto. Debe escribir exactamente: "${entidadNombre}"`);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resultadoEliminacion = await AdminGlobalService.eliminarEntidadDefinitivamente(
        tipoEntidad,
        entidadId,
        true
      );
      setResultado(resultadoEliminacion);
      if (resultadoEliminacion.exito) {
        setPaso(4);
        onSuccess?.();
        setTimeout(() => { onClose(); }, 2000);
      } else {
        setError(resultadoEliminacion.mensaje);
      }
    } catch (e: unknown) {
      setError('No pudimos ejecutar la eliminación definitiva.');
      reportarErrorInesperado(e, 'No pudimos ejecutar la eliminación definitiva.');
    } finally {
      setLoading(false);
    }
  };

  const getIconoEntidad = () => {
    switch (tipoEntidad) {
      case 'provincia': return <Database className="w-6 h-6 text-red-600" />;
      case 'diocesis':  return <Shield className="w-6 h-6 text-red-600" />;
      case 'lumisial':  return <Users className="w-6 h-6 text-red-600" />;
      default:          return <AlertTriangle className="w-6 h-6 text-red-600" />;
    }
  };

  const getNombreEntidad = () => {
    switch (tipoEntidad) {
      case 'provincia': return 'Provincia Eclesiástica';
      case 'diocesis':  return 'Diócesis';
      case 'lumisial':  return 'Lumisial';
      default:          return 'Entidad';
    }
  };

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      onBeforeClose={() => false}
      size="md"
      header={
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 flex items-center justify-between p-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              {getIconoEntidad()}
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Eliminación Definitiva</h2>
              <p className="text-sm text-slate-500">{getNombreEntidad()}: {entidadNombre}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label="Cerrar modal"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>
      }
    >
      {verificandoPermisos && (
        <div className="text-center py-8">
          <div className="p-3 bg-blue-100 rounded-lg inline-block mb-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Verificando permisos...</h3>
          <p className="text-slate-600 text-sm">Validando acceso de administrador global</p>
        </div>
      )}
      {!verificandoPermisos && esAdminGlobal !== true && (
        <div className="text-center py-8">
          <div className="p-3 bg-red-100 rounded-lg inline-block mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Acceso Restringido</h3>
          <p className="text-slate-600">
            {error || 'No tienes los permisos necesarios para realizar esta acción.'}
          </p>
        </div>
      )}
      {!verificandoPermisos && esAdminGlobal === true && (
        <>
          {/* Paso 1: Información Inicial */}
          {paso === 1 && (
            <div className="space-y-6">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-red-900 mb-2">
                      ADVERTENCIA: Eliminación Definitiva
                    </h3>
                    <p className="text-red-700 text-sm">
                      Está a punto de eliminar definitivamente una {getNombreEntidad().toLowerCase()}
                      y todos sus datos asociados. Esta acción no se puede deshacer.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-slate-900">¿Qué se eliminará?</h4>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    La {getNombreEntidad().toLowerCase()} "{entidadNombre}"
                  </li>
                  {tipoEntidad === 'lumisial' && (
                    <>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        Todos los miembros asociados
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        Todos los movimientos económicos
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        Todas las actividades económicas
                      </li>
                    </>
                  )}
                  {tipoEntidad === 'diocesis' && (
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      Todos los lumisiales de la diócesis y sus datos
                    </li>
                  )}
                  {tipoEntidad === 'provincia' && (
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      Todas las diócesis de la provincia y sus datos
                    </li>
                  )}
                </ul>
              </div>

              <FormErrorAlert error={error} />

              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={obtenerImpacto}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Analizando...' : 'Continuar'}
                </button>
              </div>
            </div>
          )}

          {/* Paso 2: Impacto de Eliminación */}
          {paso === 2 && impacto && (
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-slate-900 mb-4">Impacto de la Eliminación</h3>
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-800 text-sm">
                    {AdminGlobalService.generarResumenImpacto(impacto)}
                  </p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() => setMostrarDetalles(!mostrarDetalles)}
                    className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800"
                  >
                    {mostrarDetalles ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {mostrarDetalles ? 'Ocultar' : 'Mostrar'} detalles
                  </button>
                </div>
                {mostrarDetalles && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-900 mb-3">Detalles del Impacto:</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Tipo de entidad:</span>
                        <span className="font-medium">{getNombreEntidad()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Nombre:</span>
                        <span className="font-medium">{impacto.entidad_nombre}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">ID:</span>
                        <span className="font-mono text-xs">{impacto.entidad_id}</span>
                      </div>
                      <div className="pt-2 mt-2 border-t">
                        <h5 className="font-medium text-slate-900 mb-2">Registros que serán eliminados:</h5>
                        {Object.entries(impacto.impacto).map(([key, value]) => (
                          value > 0 && (
                            <div key={key} className="flex justify-between text-sm">
                              <span className="text-slate-600 capitalize">{key}:</span>
                              <span className="font-medium text-red-600">{value}</span>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <FormErrorAlert error={error} />

              <div className="flex gap-3">
                <button
                  onClick={() => setPaso(1)}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Atrás
                </button>
                <button
                  onClick={() => setPaso(3)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Entendido, continuar
                </button>
              </div>
            </div>
          )}

          {/* Paso 3: Confirmación Final */}
          {paso === 3 && (
            <div className="space-y-6">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h3 className="font-semibold text-red-900 mb-2">Confirmación Final Requerida</h3>
                <p className="text-red-700 text-sm">
                  Para confirmar la eliminación definitiva, escriba exactamente el nombre de la {getNombreEntidad().toLowerCase()}:
                </p>
                <div className="mt-2 p-2 bg-red-100 rounded font-mono text-sm text-red-800">
                  {entidadNombre}
                </div>
              </div>

              <div>
                <label htmlFor="confirm-delete-text" className="block text-sm font-medium text-slate-700 mb-2">
                  Texto de confirmación:
                </label>
                <input
                  id="confirm-delete-text"
                  type="text"
                  value={confirmacionTexto}
                  onChange={(e) => setConfirmacionTexto(e.target.value)}
                  placeholder={`Escriba: ${entidadNombre}`}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                />
              </div>

              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-600">
                  Al hacer clic en "Eliminar Definitivamente", se eliminarán permanentemente
                  todos los datos asociados. Esta acción no se puede deshacer.
                </p>
              </div>

              <FormErrorAlert error={error} />

              <div className="flex gap-3">
                <button
                  onClick={() => setPaso(2)}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Atrás
                </button>
                <button
                  onClick={confirmarEliminacion}
                  disabled={loading || confirmacionTexto.trim() !== entidadNombre.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Eliminando...' : 'Eliminar Definitivamente'}
                </button>
              </div>
            </div>
          )}

          {/* Paso 4: Resultado */}
          {paso === 4 && resultado && (
            <div className="space-y-6">
              <div className={`p-4 rounded-lg border ${
                resultado.exito ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${resultado.exito ? 'bg-green-100' : 'bg-red-100'}`}>
                    <AlertTriangle className={`w-5 h-5 ${resultado.exito ? 'text-green-600' : 'text-red-600'}`} />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${resultado.exito ? 'text-green-900' : 'text-red-900'}`}>
                      {resultado.exito ? 'Eliminación Completada' : 'Error en Eliminación'}
                    </h3>
                    <p className={`text-sm ${resultado.exito ? 'text-green-700' : 'text-red-700'}`}>
                      {resultado.mensaje}
                    </p>
                  </div>
                </div>
              </div>

              {resultado.exito && resultado.registros_eliminados > 0 && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Resumen de la Operación:</h4>
                  <div className="text-sm text-blue-800">
                    <p>Total de registros afectados: <strong>{resultado.registros_eliminados}</strong></p>
                    <p className="mt-1 text-xs">Esta acción ha sido registrada en el sistema de auditoría.</p>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </DialogShell>
  );
};

export default EliminacionDefinitivaModal;
